<script>
import { getRandomIntInclusive, reduceAddress } from '@/utilities'
import { NFT_INFO } from '../constants'
import { getBigmapKey } from '../services/tzkt'
import { loadOwners, fetchLastSale, OBJKT_MARKETPLACE } from '../services/txlOwners'

// TXL collection lives on mainnet, even when the rest of the app is on shadownet.
const TXL_NETWORK = 'mainnet'

// TzKT bigmap ids the TXL collection uses.
const TXL_METADATA_BIGMAP = 861 // key = kalaId,  value = { ipfs_hash, ... }
const TXL_OWNER_BIGMAP = 857 // key = { address, nat: kalaId },  value = balance

export default {
  name: 'browseNFTs',
  props: ['socket', 'wallet', 'tezos'],
  data () {
    return {
      nftInfo: NFT_INFO,
      showInfo: false, 
      txlId: 68,
      rank: 0,
      txlRanking: 1,
      txlData: {},
      owner: '',
      // Raw owner tz/KT address — kept alongside the display label so we can
      // compare against the connected wallet for the "owned by you" highlight.
      ownerAddress: '',
      // The connected wallet's address (filled from beacon on mount and on
      // socket 'newWallet' events). Empty when no wallet is connected.
      myAddress: '',
      // Collection-wide ownership metadata, loaded once via the txlOwners
      // service (snapshot for instant paint, live tzkt refresh in the
      // background). getOwner() reads `owners` instead of a per-card lookup.
      owners: {},
      topHolders: [],
      distinctHolders: 0,
      onMarketplace: 0,
      // Most recent objkt sale of any TXL NFT — null until fetchLastSale
      // resolves. Shape: { kalaId, to, timestamp, priceMutez }.
      lastSale: null,
      tezosSymbol: 'tez',
      intervalId: null,
      countDownSeconds: 5,
      pauseRandom: true,
      pauseState: 'Play',
      currentRotation: 0,
      pauseAnimation: false,
      pauseAnimationState: 'Pause',
      // URL of the current NFT image to show in the rotating CSS card.
      // Falls back to the bundled example until setNewNftImage loads
      // something from IPFS.
      currentNftSrc: require('../assets/nftExample.jpeg'),
    }
  },

  computed: {
    // Largest single holder, ignoring the objkt.com marketplace contract
    // (tokens parked there are listed for sale, not held by a collector).
    topHolderLabel() {
      const entry = this.topHolders.find(([addr]) => addr !== OBJKT_MARKETPLACE)
      return entry ? `${reduceAddress(entry[0])} (${entry[1]})` : ''
    },
    // True when the connected wallet is the owner of the currently-displayed
    // NFT — gates the green "owned by you" highlight in the stats row.
    isOwnedByMe() {
      if (!this.myAddress || !this.ownerAddress) return false
      return this.ownerAddress.toLowerCase() === this.myAddress.toLowerCase()
    },
    // True when the displayed token is parked on the objkt marketplace
    // contract (i.e. listed for sale). The Owner button uses this to
    // switch its appearance from a plain pill to the jade buy-me chip.
    isForSale() {
      return this.ownerAddress === OBJKT_MARKETPLACE
    },
    // Where the Owner pill jumps to on objkt. For a real tz1 collector
    // we open their objkt profile; when the token is parked on the
    // marketplace contract we open the listing itself (same URL as
    // the Buy on Objkt button). Empty string when there's no owner
    // resolved yet — the template treats that as non-clickable.
    ownerObjktUrl() {
      const addr = this.ownerAddress
      if (!addr) return ''
      if (addr === OBJKT_MARKETPLACE) {
        const kalaId = this.idLookUp[this.txlId]
        return kalaId ? this.objectKalaUrl + kalaId : ''
      }
      return `https://objkt.com/users/${addr}`
    },
    // Price portion of the last-sale chip, e.g. "5 tez". Empty when we don't
    // have a sale yet or the parent transaction returned no amount.
    lastSalePrice() {
      const m = this.lastSale?.priceMutez
      if (!m) return ''
      const tez = m / 1_000_000
      const formatted = tez >= 1
        ? tez.toLocaleString(undefined, { maximumFractionDigits: 2 })
        : tez.toLocaleString(undefined, { maximumFractionDigits: 4 })
      return `${formatted} ${this.tezosSymbol}`
    },
    // "Sold: TXL #N" companion to the price chip — derives the TXL # from the
    // kalaId via the existing idLookUp (no extra lookup table needed).
    lastSaleToken() {
      const kalaId = this.lastSale?.kalaId
      if (!kalaId) return ''
      const entry = Object.entries(this.idLookUp || {}).find(
        ([, k]) => String(k) === String(kalaId)
      )
      return entry ? `TXL #${entry[0]}` : `Kala #${kalaId}`
    },
    // Short relative-time string ("3d ago", "5h ago") for the sale timestamp.
    lastSaleAgo() {
      const iso = this.lastSale?.timestamp
      if (!iso) return ''
      const then = Date.parse(iso)
      if (!then) return ''
      const secs = Math.max(0, Math.floor((Date.now() - then) / 1000))
      if (secs < 60) return `${secs}s ago`
      const mins = Math.floor(secs / 60)
      if (mins < 60) return `${mins}m ago`
      const hours = Math.floor(mins / 60)
      if (hours < 24) return `${hours}h ago`
      const days = Math.floor(hours / 24)
      if (days < 30) return `${days}d ago`
      const months = Math.floor(days / 30)
      if (months < 12) return `${months}mo ago`
      return `${Math.floor(months / 12)}y ago`
    },
  },

  created () {
    // API and Static
    this.idLookUp = {
      1: '60199',
      2: '60200',
      3: '60201',
      4: '60202',
      5: '60203',
      6: '60204',
      7: '60206',
      8: '60207',
      9: '60208',
      10: '60209',
      11: '60210',
      12: '60211',
      13: '60212',
      14: '60213',
      15: '60215',
      16: '60216',
      17: '60218',
      18: '60219',
      19: '60220',
      20: '60221',
      21: '60224',
      22: '60225',
      23: '60226',
      24: '60227',
      25: '60228',
      26: '60230',
      27: '60231',
      28: '60233',
      29: '60234',
      30: '60235',
      31: '60236',
      32: '60237',
      33: '60238',
      34: '60239',
      35: '60240',
      36: '60241',
      37: '60242',
      38: '60243',
      39: '60244',
      40: '60245',
      41: '60246',
      42: '60247',
      43: '60248',
      44: '60249',
      45: '60250',
      46: '60251',
      47: '60252',
      48: '60253',
      49: '60254',
      50: '60255',
      51: '60256',
      52: '60257',
      53: '60258',
      54: '60259',
      55: '60260',
      56: '60338',
      57: '60339',
      58: '60340',
      59: '60344',
      60: '60346',
      61: '60348',
      62: '60349',
      63: '60350',
      64: '60354',
      65: '60355',
      66: '60356',
      67: '60357',
      68: '60358',
      69: '60359',
      70: '60361',
      71: '60362',
      72: '60363',
      73: '60366',
      74: '60367',
      75: '60368',
      76: '60369',
      77: '60370',
      78: '60371',
      79: '60372',
      80: '60373',
      81: '60374',
      82: '60375',
      83: '60377',
      84: '60379',
      85: '60380',
      86: '60381',
      87: '60382',
      88: '60383',
      89: '60384',
      90: '60386',
      91: '60387',
      92: '60388',
      93: '60389',
      94: '60391',
      95: '60392',
      96: '60393',
      97: '60394',
      98: '60395',
      99: '60396',
      100: '60397',
      101: '60399',
      102: '60401',
      103: '60403',
      104: '60404',
      105: '60406',
      106: '60407',
      107: '60413',
      108: '60414',
      109: '60416',
      110: '60418',
      111: '60429',
      112: '60432',
      113: '60433',
      114: '60434',
      115: '60436',
      116: '60437',
      117: '60438',
      118: '60439',
      119: '60440',
      120: '60441',
      121: '60442',
      122: '60443',
      123: '60444',
      124: '60445',
      125: '60446',
      126: '60447',
      127: '60448',
      128: '60449',
      129: '60450',
      130: '60451',
      131: '60452',
      132: '60453',
      133: '60454',
      134: '60455',
      135: '60456',
      136: '60457',
      137: '60458',
      138: '60459',
      139: '60460',
      140: '60461',
      141: '60462',
      142: '60463',
      143: '60464',
      144: '60465',
      145: '60466',
      146: '60467',
      147: '60468',
      148: '60469',
      149: '60470',
      150: '60471',
      151: '60472',
      152: '60473',
      153: '60474',
      154: '60475',
      155: '60476',
      156: '60477',
      157: '60478',
      158: '60479',
      159: '60480',
      160: '60481',
      161: '60483',
      162: '60486',
      163: '60487',
      164: '60489',
      165: '60491',
      166: '60492',
      167: '60493',
      168: '60494',
      169: '60495',
      170: '60496',
      171: '60497',
      172: '60498',
      173: '60499',
      174: '60500',
      175: '60501',
      176: '60502',
      177: '60534',
      178: '60535',
      179: '60536',
      180: '60537',
      181: '60545',
      182: '60547',
      183: '60546',
      184: '60548',
      185: '60549',
      186: '60550',
      187: '60551',
      188: '60552',
      189: '60553',
      190: '60554',
      191: '60560',
      192: '60561',
      193: '60562',
      194: '60563',
      195: '60564',
      196: '60565',
      197: '60566',
      198: '60567',
      199: '60571',
      200: '60572',
      201: '60573',
      202: '60575',
      203: '60576',
      204: '60577',
      205: '60578',
      206: '60580',
      207: '60581',
      208: '60582',
      209: '60583',
      210: '60584',
      211: '60585',
      212: '60586',
      213: '60587',
      214: '60589',
      215: '60590',
      216: '60593',
      217: '60595',
      218: '60596',
      219: '60597',
      220: '60599',
      221: '60600',
      222: '60601',
      223: '60603',
      224: '60605',
      225: '60606',
      226: '60607',
      227: '60608',
      228: '60612',
      229: '60613',
      230: '60614',
      231: '60615',
      232: '60616',
      233: '60617',
      234: '60618',
      235: '60619',
      236: '60620',
      237: '60621',
      238: '60622',
      239: '60623',
      240: '60624',
      241: '60625',
      242: '60626',
      243: '60627',
      244: '60628',
      245: '60629',
      246: '60630',
      247: '60631',
      248: '60632',
      249: '60633',
      251: '60636',
      252: '60637',
      253: '60638',
      254: '60639',
      255: '60640',
      256: '60641',
      257: '60642',
      258: '60643',
      259: '60644',
      260: '60645',
      261: '60646',
      262: '60647',
      263: '60648',
      264: '60649',
      265: '60650',
      266: '60651',
      267: '60688',
      268: '60690',
      269: '60692',
      270: '60693',
      271: '60694',
      272: '60696'
    }
    this.txlRankings= {
      "1": "139",
      "10": "211",
      "100": "118",
      "101": "256",
      "102": "57",
      "103": "244",
      "104": "254",
      "105": "16",
      "106": "62",
      "107": "129",
      "108": "124",
      "109": "71",
      "11": "45",
      "110": "105",
      "111": "210",
      "112": "23",
      "113": "203",
      "114": "70",
      "115": "109",
      "116": "44",
      "117": "242",
      "118": "252",
      "119": "222",
      "12": "14",
      "120": "102",
      "121": "243",
      "122": "266",
      "123": "55",
      "124": "130",
      "125": "114",
      "126": "13",
      "127": "219",
      "128": "174",
      "129": "228",
      "13": "68",
      "130": "88",
      "131": "265",
      "132": "142",
      "133": "165",
      "134": "111",
      "135": "26",
      "136": "64",
      "137": "163",
      "138": "182",
      "139": "136",
      "14": "195",
      "140": "220",
      "141": "50",
      "142": "36",
      "143": "9",
      "144": "1",
      "145": "53",
      "146": "95",
      "147": "106",
      "148": "158",
      "149": "206",
      "15": "125",
      "150": "177",
      "151": "66",
      "152": "82",
      "153": "69",
      "154": "104",
      "155": "188",
      "156": "52",
      "157": "48",
      "158": "160",
      "159": "108",
      "16": "27",
      "160": "236",
      "161": "24",
      "162": "190",
      "163": "267",
      "164": "198",
      "165": "87",
      "166": "178",
      "167": "38",
      "168": "37",
      "169": "260",
      "17": "224",
      "170": "217",
      "171": "63",
      "172": "185",
      "173": "39",
      "174": "74",
      "175": "115",
      "176": "131",
      "177": "81",
      "178": "240",
      "179": "90",
      "18": "33",
      "180": "91",
      "181": "153",
      "182": "147",
      "183": "169",
      "184": "253",
      "185": "47",
      "186": "76",
      "187": "231",
      "188": "43",
      "189": "259",
      "19": "189",
      "190": "92",
      "191": "83",
      "192": "42",
      "193": "138",
      "194": "214",
      "195": "152",
      "196": "179",
      "197": "186",
      "198": "235",
      "199": "237",
      "2": "40",
      "20": "107",
      "200": "167",
      "201": "119",
      "202": "29",
      "203": "10",
      "204": "41",
      "205": "146",
      "206": "123",
      "207": "212",
      "208": "94",
      "209": "234",
      "21": "207",
      "210": "162",
      "211": "101",
      "212": "150",
      "213": "204",
      "214": "216",
      "215": "261",
      "216": "144",
      "217": "205",
      "218": "132",
      "219": "200",
      "22": "59",
      "220": "172",
      "221": "148",
      "222": "181",
      "223": "85",
      "224": "232",
      "225": "180",
      "226": "258",
      "227": "134",
      "228": "264",
      "229": "86",
      "23": "120",
      "230": "20",
      "231": "72",
      "232": "116",
      "233": "225",
      "234": "248",
      "235": "100",
      "236": "28",
      "237": "80",
      "238": "32",
      "239": "249",
      "24": "112",
      "240": "110",
      "241": "60",
      "242": "193",
      "243": "226",
      "244": "245",
      "245": "271",
      "246": "121",
      "247": "187",
      "248": "17",
      "249": "122",
      "25": "54",
      "250": "218",
      "251": "89",
      "252": "213",
      "253": "97",
      "254": "61",
      "255": "215",
      "256": "170",
      "257": "7",
      "258": "155",
      "259": "246",
      "26": "173",
      "260": "201",
      "261": "77",
      "262": "161",
      "263": "113",
      "264": "140",
      "265": "25",
      "266": "98",
      "267": "128",
      "268": "126",
      "269": "65",
      "27": "11",
      "270": "22",
      "271": "75",
      "272": "241",
      "28": "154",
      "29": "227",
      "3": "247",
      "30": "229",
      "31": "197",
      "32": "239",
      "33": "5",
      "34": "51",
      "35": "250",
      "36": "4",
      "37": "67",
      "38": "251",
      "39": "46",
      "4": "175",
      "40": "3",
      "41": "6",
      "42": "103",
      "43": "263",
      "44": "262",
      "45": "117",
      "46": "176",
      "47": "133",
      "48": "194",
      "49": "268",
      "5": "184",
      "50": "12",
      "51": "141",
      "52": "137",
      "53": "171",
      "54": "192",
      "55": "164",
      "56": "233",
      "57": "159",
      "58": "2",
      "59": "151",
      "6": "156",
      "60": "31",
      "61": "15",
      "62": "223",
      "63": "8",
      "64": "49",
      "65": "238",
      "66": "221",
      "67": "157",
      "68": "0",
      "69": "127",
      "7": "84",
      "70": "78",
      "71": "255",
      "72": "30",
      "73": "93",
      "74": "145",
      "75": "34",
      "76": "18",
      "77": "143",
      "78": "183",
      "79": "35",
      "8": "58",
      "80": "135",
      "81": "208",
      "82": "199",
      "83": "270",
      "84": "166",
      "85": "56",
      "86": "257",
      "87": "202",
      "88": "19",
      "89": "269",
      "9": "196",
      "90": "99",
      "91": "168",
      "92": "73",
      "93": "191",
      "94": "79",
      "95": "149",
      "96": "96",
      "97": "21",
      "98": "209",
      "99": "230"
    }
    this.txlRevRankings  = {
      1: 68,
      2: 144,
      3: 58,
      4: 40,
      5: 36,
      6: 33,
      7: 41,
      8: 257,
      9: 63,
      10: 143,
      11: 203,
      12: 27,
      13: 50,
      14: 126,
      15: 12,
      16: 61,
      17: 105,
      18: 248,
      19: 76,
      20: 88,
      21: 230,
      22: 97,
      23: 270,
      24: 112,
      25: 161,
      26: 265,
      27: 135,
      28: 16,
      29: 236,
      30: 202,
      31: 72,
      32: 60,
      33: 238,
      34: 18,
      35: 75,
      36: 79,
      37: 142,
      38: 168,
      39: 167,
      40: 173,
      41: 2,
      42: 204,
      43: 192,
      44: 188,
      45: 116,
      46: 11,
      47: 39,
      48: 185,
      49: 157,
      50: 64,
      51: 141,
      52: 34,
      53: 156,
      54: 145,
      55: 25,
      56: 123,
      57: 85,
      58: 102,
      59: 8,
      60: 22,
      61: 241,
      62: 254,
      63: 106,
      64: 171,
      65: 136,
      66: 269,
      67: 151,
      68: 37,
      69: 13,
      70: 153,
      71: 114,
      72: 109,
      73: 231,
      74: 92,
      75: 174,
      76: 271,
      77: 186,
      78: 261,
      79: 70,
      80: 94,
      81: 237,
      82: 177,
      83: 152,
      84: 191,
      85: 7,
      86: 223,
      87: 229,
      88: 165,
      89: 130,
      90: 251,
      91: 179,
      92: 180,
      93: 190,
      94: 73,
      95: 208,
      96: 146,
      97: 96,
      98: 253,
      99: 266,
      100: 90,
      101: 235,
      102: 211,
      103: 120,
      104: 42,
      105: 154,
      106: 110,
      107: 147,
      108: 20,
      109: 159,
      110: 115,
      111: 240,
      112: 134,
      113: 24,
      114: 263,
      115: 125,
      116: 175,
      117: 232,
      118: 45,
      119: 100,
      120: 201,
      121: 23,
      122: 246,
      123: 249,
      124: 206,
      125: 108,
      126: 15,
      127: 268,
      128: 69,
      129: 267,
      130: 107,
      131: 124,
      132: 176,
      133: 218,
      134: 47,
      135: 227,
      136: 80,
      137: 139,
      138: 52,
      139: 193,
      140: 1,
      141: 264,
      142: 51,
      143: 132,
      144: 77,
      145: 216,
      146: 74,
      147: 205,
      148: 182,
      149: 221,
      150: 95,
      151: 212,
      152: 59,
      153: 195,
      154: 181,
      155: 28,
      156: 258,
      157: 6,
      158: 67,
      159: 148,
      160: 57,
      161: 158,
      162: 262,
      163: 210,
      164: 137,
      165: 55,
      166: 133,
      167: 84,
      168: 200,
      169: 91,
      170: 183,
      171: 256,
      172: 53,
      173: 220,
      174: 26,
      175: 128,
      176: 4,
      177: 46,
      178: 150,
      179: 166,
      180: 196,
      181: 225,
      182: 222,
      183: 138,
      184: 78,
      185: 5,
      186: 172,
      187: 197,
      188: 247,
      189: 155,
      190: 19,
      191: 162,
      192: 93,
      193: 54,
      194: 242,
      195: 48,
      196: 14,
      197: 9,
      198: 31,
      199: 164,
      200: 82,
      201: 219,
      202: 260,
      203: 87,
      204: 113,
      205: 213,
      206: 217,
      207: 149,
      208: 21,
      209: 81,
      210: 98,
      211: 111,
      212: 10,
      213: 207,
      214: 252,
      215: 194,
      216: 255,
      217: 214,
      218: 170,
      219: 250,
      220: 127,
      221: 140,
      222: 66,
      223: 119,
      224: 62,
      225: 17,
      226: 233,
      227: 243,
      228: 29,
      229: 129,
      230: 30,
      231: 99,
      232: 187,
      233: 224,
      234: 56,
      235: 209,
      236: 198,
      237: 160,
      238: 199,
      239: 65,
      240: 32,
      241: 178,
      242: 272,
      243: 117,
      244: 121,
      245: 103,
      246: 244,
      247: 259,
      248: 3,
      249: 234,
      250: 239,
      251: 35,
      252: 38,
      253: 118,
      254: 184,
      255: 104,
      256: 71,
      257: 101,
      258: 86,
      259: 226,
      260: 189,
      261: 169,
      262: 215,
      263: 44,
      264: 43,
      265: 228,
      266: 131,
      267: 122,
      268: 163,
      269: 49,
      270: 89,
      271: 83,
      272: 245
    }
    this.selectRandom()
    this.objectUrl =
      'https://objkt.com/users/tz1Vq5mYKXw1dD9js26An8dXdASuzo3bfE2w?fa_contract=KT1EpGgjQs73QfFJs9z7m1Mxm5MTnpC2tqse&availability=for_sale'
    this.objectKalaUrl = 'https://objkt.com/tokens/kalamint/'
    this.ipfsHttpsLink = 'https://ipfs.io/ipfs/'
    this.rankingsHashUrl = 'https://ipfs.io/ipfs/QmTY4jY9q4XwcyEVNfsQJj8t4Liesi7nhzKcqyPYwMdm1t'

    // The NFT card is a CSS 3D element (see template + styles). The socket
    // 'resizeGame' listener is preserved for compatibility with the server
    // fanout, but it's a no-op now — CSS handles sizing via clamp().
    this.socket.on('resizeGame', (width) => {
      this.resizeGameRender(width)
    })

    // Pull the whole {kalaId → owner} map plus holder stats in one shot.
    // The snapshot paints instantly; the live tzkt refresh updates in the
    // background. getOwner() then reads this map — no per-card network call.
    loadOwners({
      onUpdate: ({ owners, topHolders, distinctHolders, onMarketplace }) => {
        this.owners = owners
        this.topHolders = topHolders
        this.distinctHolders = distinctHolders
        this.onMarketplace = onMarketplace
        this.refreshOwnerLabel()
      },
    })

    // Most recent objkt sale across the whole collection. Fire-and-forget;
    // the template falls back gracefully when lastSale is still null.
    fetchLastSale().then((sale) => {
      if (sale) this.lastSale = sale
    })

    // mainBody re-broadcasts wallet connect/disconnect via this socket event.
    this.socket.on('newWallet', () => { this.refreshMyAddress() })
  },
  mounted() {
    // Single 1-second tick drives both the visible countdown and the actual
    // NFT swap, so they can't drift apart. When countDownSeconds hits zero,
    // tick() fires selectRandom() and resets the timer. Previously these
    // were two independent setIntervals started a few ms apart in mounted()
    // — they drifted over time and pause/resume could desync them further.
    this.tickInterval = setInterval(() => this.tick(), 1000)
    this.refreshMyAddress()
    this.selectRandom()
    this.getNftData()
  },
  methods: {
    // Trait values arrive as "Black (66.18%)" — split the visible value from
    // the embedded rarity percent (lower percent = rarer trait).
    traitValue(raw) {
      if (raw == null) return ''
      const m = String(raw).match(/^(.*)\s*\(\s*[\d.]+\s*%\s*\)\s*$/)
      return m ? m[1].trim() : String(raw)
    },
    traitRarity(raw) {
      if (raw == null) return null
      const m = String(raw).match(/\(\s*([\d.]+)\s*%\s*\)/)
      return m ? Number(m[1]) : null
    },
    rarityTier(raw) {
      const p = this.traitRarity(raw)
      if (p == null) return 'rarityNone'
      if (p <= 1)  return 'rarityLegendary'
      if (p <= 5)  return 'rarityEpic'
      if (p <= 15) return 'rarityRare'
      if (p <= 35) return 'rarityUncommon'
      return 'rarityCommon'
    },
    // CSS animation handles the spin — we only flip `pauseAnimation`, which
    // toggles `animation-play-state` on the rotating card via :class binding.
    async toggleAnimation() {
      if (this.pauseAnimation) {
        this.pauseAnimation = false
        this.pauseAnimationState = 'Pause'
      } else {
        this.pauseAnimation = true
        this.pauseAnimationState = 'Animate'
      }
    },
    async browseAllOnObjkt () {
      window.open(this.objectUrl, '_blank');
    },
    async checkThisOnObjkt () {
      const kalaId = this.idLookUp[this.txlId]
      window.open(this.objectKalaUrl + kalaId, '_blank');
    },
    // Kept so the socket 'resizeGame' handler still resolves cleanly.
    // CSS clamp() handles all real sizing now.
    resizeGameRender(_width) {
      // intentionally empty
    },
    async getNftDataId() {
      await this.getNftData()
    },
    async getNftDataRank() {
      this.txlId = this.txlRevRankings[this.txlRanking] 
      await this.getNftData()
    },
    async getNftData() {
      await this.setNewNftImage() 
      await this.getNftMetaData() 
      await this.getOwner()
      this.txlRanking = Number(this.txlRankings[this.txlId.toString()]) + 1
    },
    async getNftMetaData() {
      const response = await fetch(this.rankingsHashUrl);
      const data = await response.json();
      this.txlData = await data[this.txlId]
    },
    async getIpfsHash() {
      const kalaId = this.idLookUp[this.txlId]
      const data = await getBigmapKey(TXL_METADATA_BIGMAP, kalaId, {}, { network: TXL_NETWORK })
      return data?.[0]?.value?.ipfs_hash ?? null
    },
    // Turn a raw owner address into the label shown on the card. Tokens held
    // by the objkt.com marketplace contract are listed for sale, not owned by
    // a collector — surface that instead of a meaningless KT1.
    labelForOwner(address) {
      if (!address) return 'unknown'
      if (address === OBJKT_MARKETPLACE) return 'FOR SALE'
      return reduceAddress(address)
    },
    // Recompute the owner label for the NFT currently on screen from the
    // in-memory owners map — called when loadOwners() delivers fresh data.
    refreshOwnerLabel() {
      const address = this.owners[this.idLookUp[this.txlId]]
      if (address) {
        this.ownerAddress = address
        this.owner = this.labelForOwner(address)
      }
    },
    async getOwner() {
      const kalaId = this.idLookUp[this.txlId]
      // Prefer the collection-wide owners map — no network call per card.
      let address = this.owners[kalaId]
      if (!address) {
        // Map hasn't loaded yet (first paint): fall back to a direct bigmap
        // lookup for just this token. key = { address, nat }, value = balance;
        // filter to active rows where balance == 1, then read key.address.
        const data = await getBigmapKey(
          TXL_OWNER_BIGMAP,
          kalaId,
          { active: 'true', 'value.eq': '1', select: 'key', 'key.nat.eq': String(kalaId) },
          { network: TXL_NETWORK }
        )
        address = data?.[0]?.address ?? data?.[0]?.key?.address
      }
      this.ownerAddress = address || ''
      this.owner = this.labelForOwner(address)
    },
    // Read the active wallet address from beacon. Called on mount and on the
    // 'newWallet' socket event so the "owned by you" highlight reacts when
    // the user connects, disconnects, or switches accounts.
    async refreshMyAddress() {
      try {
        const acct = await this.wallet?.client?.getActiveAccount?.()
        this.myAddress = acct?.address || ''
      } catch {
        this.myAddress = ''
      }
    },
    async setNewNftImage() {
      const ipfsHash = await this.getIpfsHash()
      if (!ipfsHash) return
      const parts = ipfsHash.split('//')
      // The browser fetches the image directly — no texture loader needed.
      // currentNftSrc is reactive, so the <img> in the rotating card updates.
      this.currentNftSrc = this.ipfsHttpsLink + (parts[1] ?? parts[0])
    },
    async selectRandom() {
      const newId = await getRandomIntInclusive(1, 273)
      this.txlId = newId
      this.getNftData()
      // The tick() handler owns countdown resets so the display can't drift.
    },
    async togglePauseRandom() {
      if (this.pauseRandom) {
        // Resuming — start from a clean 5 so the user sees the full countdown.
        this.countDownSeconds = 5
        this.pauseRandom = false
        this.pauseState = 'Pause'
      } else {
        this.pauseRandom = true
        this.pauseState = 'Play'
      }
    },
    async prevTxl() {  
      this.txlId -= 1
      if (this.txlId < 0) {
        this.txlId = 0
      }    
      this.getNftData()  
    },
    async nextTxl() {  
      this.txlId += 1
      if (this.txlId > 273) {
        this.txlId = 273
      }    
      this.getNftData()  
    },
    async prevRank() {  
      this.txlRanking -= 1
      this.txlId = this.txlRevRankings[this.txlRanking]     
      this.getNftData()  
    },
    async nextRank() {  
      this.txlRanking += 1
      this.txlId = this.txlRevRankings[this.txlRanking] 
      this.getNftData()  
    },
    async showLearnMore() {
      if (this.showInfo)  {
        this.showInfo = false
      } else {
        this.showInfo = true
      }
    },
    // Single source of truth for "random NFT in N seconds". Fires once per
    // second. When the timer expires we swap to a new random NFT and reset
    // the visible countdown — so the on-screen number and the actual swap
    // are guaranteed to agree.
    tick() {
      if (this.pauseRandom) return
      this.countDownSeconds -= 1
      if (this.countDownSeconds <= 0) {
        this.selectRandom()
        this.countDownSeconds = 5
      }
    },
  },
  beforeUnmount() {
    if (this.tickInterval) clearInterval(this.tickInterval)
  },
}
</script>

<template>
 
  <div class="centerBody">
    <div class="gameManagement" >
        <div class="nftStageRow">
          <div class="navColumn">
            <div class="navColumnLabel">Rank</div>
            <div class="actionButton navColumnBtn" @click="prevRank()"> &larr; </div>
            <select class="slotReel navColumnSelect" v-model="txlRanking" @change="getNftDataRank()">
              <option v-for="(key, value) in txlRevRankings" :key="key" :value="value"> {{ value }} </option>
            </select>
            <div class="actionButton navColumnBtn" @click="nextRank()"> &rarr; </div>
          </div>
          <div class="cardArea">
            <div class="nftCardStage">
              <div :class="['nftCard', pauseAnimation ? 'nftCard--paused' : '']">
                <div class="nftCardFace nftCardFace--front">
                  <img
                    :src="currentNftSrc"
                    :alt="'TXL #' + txlId"
                    class="nftCardImg"
                    draggable="false"
                  />
                </div>
                <div class="nftCardFace nftCardFace--back">
                  <img
                    :src="currentNftSrc"
                    :alt="'TXL #' + txlId"
                    class="nftCardImg"
                    draggable="false"
                  />
                </div>
              </div>
              <div class="nftCardShadow" aria-hidden="true"></div>
            </div>
          </div>
          <div class="navColumn">
            <div class="navColumnLabel">ID</div>
            <div class="actionButton navColumnBtn" @click="prevTxl()"> &larr; </div>
            <select class="slotReel navColumnSelect" v-model="txlId" @change="getNftDataId()">
              <option v-for="(key, value) in idLookUp" :key="key" :value="value"> {{ value }} </option>
            </select>
            <div class="actionButton navColumnBtn" @click="nextTxl()"> &rarr; </div>
          </div>
        </div>
        <!-- NFT stats: per-token attributes (rank, ID, traits). Collection-
             wide numbers live in the second grid below so the two scopes
             don't blur together. -->
        <div class="statsSection">
          <div class="statsLabel">NFT</div>
          <div class="attrGrid">
            <div class="attrCell attrCell--meta">
              <span class="attrLabel">Rank:</span>
              <span class="attrValue">{{ txlRanking }}</span>
            </div>
            <div class="attrCell attrCell--meta">
              <span class="attrLabel">ID:</span>
              <span class="attrValue">{{ txlId }}</span>
            </div>
            <div
              v-for="(raw, key) in txlData"
              :key="key"
              :class="['attrCell', rarityTier(raw)]"
            >
              <span class="attrLabel">{{ key }}:</span>
              <span class="attrValue">{{ traitValue(raw) }}</span>
              <span class="attrRarity" v-if="traitRarity(raw) != null">
                {{ traitRarity(raw).toFixed(2) }}%
              </span>
            </div>
          </div>
        </div>
        <!-- Collection stats: numbers that describe the whole TXL set, not
             the currently-shown token. -->
        <div
          class="statsSection"
          v-if="distinctHolders || onMarketplace || topHolderLabel || lastSale"
        >
          <div class="statsLabel">Collection</div>
          <div class="attrGrid">
            <div class="attrCell attrCell--meta" v-if="distinctHolders">
              <span class="attrLabel">Holders:</span>
              <span class="attrValue">{{ distinctHolders }}</span>
            </div>
            <div class="attrCell attrCell--meta" v-if="onMarketplace">
              <span class="attrLabel">Listed on objkt:</span>
              <span class="attrValue">{{ onMarketplace }}</span>
            </div>
            <div class="attrCell attrCell--meta" v-if="topHolderLabel">
              <span class="attrLabel">Top holder:</span>
              <span class="attrValue">{{ topHolderLabel }}</span>
            </div>
            <div class="attrCell attrCell--meta" v-if="lastSalePrice">
              <span class="attrLabel">Last sale:</span>
              <span class="attrValue">
                {{ lastSalePrice }}<template v-if="lastSaleToken"> · {{ lastSaleToken }}</template>
              </span>
              <span class="attrRarity attrRarity--neutral" v-if="lastSaleAgo">
                {{ lastSaleAgo }}
              </span>
            </div>
          </div>
        </div>
        <!-- Bottom action row: Owner (linked to objkt when resolvable) +
             Learn more. Side-by-side on desktop, stacked on mobile via
             .txlActionRow--stack. -->
        <div class="rowFlex txlActionRow txlActionRow--stack">
          <a
            v-if="owner && ownerObjktUrl"
            :class="[
              'actionButton',
              'txlOwnerBtn',
              isForSale ? 'txlOwnerBtn--forSale' : '',
              isOwnedByMe ? 'txlOwnerBtn--mine' : '',
            ]"
            :href="ownerObjktUrl"
            target="_blank"
            rel="noopener noreferrer"
            :title="ownerAddress + ' — open on objkt'"
          >
            <template v-if="isForSale">
              <span>For sale — view on objkt</span>
              <span class="txlOwnerBtn__ext" aria-hidden="true">↗</span>
            </template>
            <template v-else-if="isOwnedByMe">
              <span>Owner: You ({{ owner }})</span>
            </template>
            <template v-else>
              <span>Owner: {{ owner }}</span>
            </template>
          </a>
          <div
            v-else
            :class="['actionButton', 'txlOwnerBtn', 'txlOwnerBtn--idle']"
            :title="ownerAddress || 'Owner not loaded yet'"
            aria-disabled="true"
          >Owner: {{ owner || '—' }}</div>
          <div class="actionButtonHelp" @click="showLearnMore">Learn more</div>
        </div>
        <div v-if="showInfo" @click="showLearnMore" class="infoPopup">
          <div>
            <ul >
              <li class="listItem" v-for="(key, value) in nftInfo" :key="key" :value="value">{{ key }}</li>
            </ul>
          </div>
        </div>
      </div>
  </div>
</template>

<style scoped>
/* Three-column row: rank nav | spinning NFT | ID nav. On phones the nav
   columns drop below the card so the NFT keeps its size. */
.nftStageRow {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  width: 100%;
  margin: 8px 0;
}
.navColumn {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 0 0 auto;
  width: clamp(56px, 9vw, 80px);
}
.navColumnLabel {
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--ad-text-2, var(--ad-text-1));
  padding-bottom: 2px;
}
.navColumnBtn {
  width: 100%;
  padding: 8px 4px;
  min-width: 0;
  flex: 0 0 auto;
  font-size: 15px;
  line-height: 1;
}
.navColumnSelect { width: 100%; min-width: 0; }
/* Slot-machine LED reel for the rank/ID picker. Still a native <select>, so
   clicking it opens the full list — only the closed-state visuals change. */
.slotReel {
  width: 100%;
  min-width: 0;
  padding: 8px 4px;
  text-align: center;
  text-align-last: center;
  font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #ffd76a;
  text-shadow: 0 0 6px rgba(255, 215, 106, 0.55), 0 0 1px rgba(255, 215, 106, 0.9);
  background:
    repeating-linear-gradient(
      0deg,
      rgba(255, 255, 255, 0.025) 0px,
      rgba(255, 255, 255, 0.025) 1px,
      transparent 1px,
      transparent 3px
    ),
    linear-gradient(180deg, #0a0a14 0%, #14101e 50%, #0a0a14 100%);
  border: 1px solid rgba(255, 200, 80, 0.35);
  border-radius: 6px;
  box-shadow:
    inset 0 0 14px rgba(0, 0, 0, 0.85),
    inset 0 1px 0 rgba(255, 255, 255, 0.06),
    0 0 8px rgba(255, 175, 40, 0.15);
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
}
.slotReel:hover {
  border-color: rgba(255, 200, 80, 0.6);
  box-shadow:
    inset 0 0 14px rgba(0, 0, 0, 0.85),
    inset 0 1px 0 rgba(255, 255, 255, 0.06),
    0 0 14px rgba(255, 175, 40, 0.4);
}
.slotReel:focus {
  outline: none;
  border-color: #ffd76a;
}
.slotReel option {
  background: #0a0a14;
  color: #ffd76a;
  font-weight: 700;
}
@media (max-width: 600px) {
  .nftStageRow {
    flex-direction: column;
    gap: 10px;
    max-width: 100%;
    min-width: 0;
  }
  .navColumn {
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
    gap: 6px;
  }
  .navColumnLabel {
    flex: 0 0 auto;
    min-width: 36px;
    text-align: left;
    padding: 0;
  }
  /* Reset the desktop-narrow width:100% on button/select so flex sizing wins
     in the horizontal mobile layout — otherwise every item tries to be full
     row width and the row blows out. */
  .navColumnBtn {
    flex: 0 0 auto;
    width: auto;
    min-width: 36px;
    padding: 8px 10px;
  }
  .navColumnSelect {
    flex: 1 1 auto;
    width: auto;
    min-width: 0;
  }
}
/* Two stacked stats panels: NFT (this token) and Collection (whole set).
   The small label sits above each grid so the scope is obvious without
   forcing readers to parse the cells. */
.statsSection {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.statsSection + .statsSection { margin-top: 8px; }
.statsLabel {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ad-text-2, var(--ad-text-1));
  padding-left: 2px;
}
.attrRarity--neutral {
  color: var(--ad-text-2, #b8b8b8);
  background: rgba(255, 255, 255, 0.05);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.10);
}
/* Trait grid — auto-flowing columns of "Label: value  XX%" inside a single
   panel. Each cell is one line of text with a colored rarity pill on the
   right; the grid tracks reflow to 1/2/3 columns based on width. */
.attrGrid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  column-gap: 18px;
  row-gap: 2px;
  width: 100%;
  padding: 8px 12px;
  background: var(--ad-bg-elev-1);
  border: 1px solid var(--ad-border-faint);
  border-radius: var(--ad-r-md);
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--ad-text-1);
}
.attrCell {
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
}
.attrCell--meta { font-weight: 600; }
.attrLabel {
  color: var(--ad-text-2, var(--ad-text-1));
  font-weight: 600;
  flex-shrink: 0;
}
.attrValue {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.attrRarity {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  font-size: 11px;
  padding: 1px 7px;
  border-radius: 999px;
  flex-shrink: 0;
}
/* Rarity tiers — lower percent = rarer = warmer/brighter accent. */
.rarityLegendary .attrRarity {
  color: #1a1208;
  background: linear-gradient(135deg, #ffd76a, #f4a300);
  box-shadow: 0 0 0 1px rgba(255, 200, 80, 0.5), 0 0 10px rgba(255, 175, 40, 0.35);
}
.rarityEpic .attrRarity {
  color: #f5e8ff;
  background: rgba(168, 85, 247, 0.22);
  box-shadow: inset 0 0 0 1px rgba(168, 85, 247, 0.55);
}
.rarityRare .attrRarity {
  color: #dceaff;
  background: rgba(59, 130, 246, 0.22);
  box-shadow: inset 0 0 0 1px rgba(59, 130, 246, 0.55);
}
.rarityUncommon .attrRarity {
  color: #d8f5e1;
  background: rgba(34, 197, 94, 0.20);
  box-shadow: inset 0 0 0 1px rgba(34, 197, 94, 0.45);
}
.rarityCommon .attrRarity {
  color: var(--ad-text-2, #b8b8b8);
  background: rgba(255, 255, 255, 0.05);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.10);
}
@media (max-width: 480px) {
  /* Safety net: any rogue child that ignores width constraints can't push
     the page wider than the viewport. */
  .centerBody {
    overflow-x: hidden;
    max-width: 100%;
    min-width: 0;
  }
  /* iOS Safari sometimes uses option intrinsic widths on appearance:none
     selects. Cap it absolutely so the slot reel can't blow out the row.
     Keep font-size 16px so iOS doesn't auto-zoom when tapping the select. */
  .slotReel {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
    font-size: 16px;
    padding: 7px 4px;
  }
  .attrGrid {
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    column-gap: 10px;
    padding: 6px 8px;
    font-size: 11.5px;
    box-sizing: border-box;
    max-width: 100%;
  }
  .attrCell { min-width: 0; }
  .attrRarity { font-size: 10.5px; padding: 1px 6px; }
}
/* Full-width band around the spinning card — anchors the owner badge to the
   right edge of the content, not the narrow centered card. */
.cardArea {
  position: relative;
  width: 100%;
}
/* Inside the 3-column nav row, the card should hug its intrinsic width so
   the flanking nav columns get their natural space. */
.nftStageRow .cardArea {
  width: auto;
  flex: 0 1 auto;
}
.nftStageRow .nftCardStage {
  margin: 0 auto;
}
/* Owner chip highlight — lights up green when the connected wallet matches
   the current NFT's owner address. */
.txlRank--mine {
  color: #4ade80;
  background: rgba(74, 222, 128, 0.14);
  border-color: rgba(74, 222, 128, 0.6);
  box-shadow:
    inset 0 0 0 1px rgba(74, 222, 128, 0.35),
    0 0 12px rgba(74, 222, 128, 0.25);
  font-weight: 700;
}
/* CSS 3D NFT card. Replaces the Three.js WebGLRenderer + rotating box. */
.nftCardStage {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 16px auto;
  perspective: 1200px;
  /* Reserve space so layout doesn't reflow mid-spin. */
  width: clamp(180px, 60vw, 320px);
}
.nftCard {
  position: relative; /* anchor for .nftCardFace inset:0 */
  width: 100%;
  aspect-ratio: 1 / 1;
  transform-style: preserve-3d;
  animation: nftSpin 6s linear infinite;
  will-change: transform;
  /* Flex items default to min-width:auto = content size. Without this, the
     intrinsic size of the bundled example image briefly pushes the card to
     ~2000px before the first paint settles. */
  min-width: 0;
  min-height: 0;
}
.nftCard--paused {
  animation-play-state: paused;
}
@keyframes nftSpin {
  from { transform: rotateY(0deg); }
  to   { transform: rotateY(360deg); }
}
.nftCardFace {
  position: absolute;
  inset: 0;
  border-radius: 6px;
  overflow: hidden;
  background: #0a0a0a;
  box-shadow:
    0 12px 30px rgba(0, 0, 0, 0.55),
    inset 0 0 0 1px rgba(255, 255, 255, 0.06);
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
}
.nftCardFace--back {
  transform: rotateY(180deg);
}
.nftCardImg {
  width: 100%;
  height: 100%;
  max-width: 100%;  /* belt-and-suspenders against intrinsic overflow */
  max-height: 100%;
  object-fit: cover;
  display: block;
  user-select: none;
  -webkit-user-drag: none;
}
.nftCardShadow {
  width: 70%;
  height: 14px;
  margin-top: 14px;
  border-radius: 50%;
  background: radial-gradient(ellipse at center, rgba(0, 0, 0, 0.55) 0%, rgba(0, 0, 0, 0) 70%);
  filter: blur(3px);
  /* Subtle pulse to suggest the card is hovering. */
  animation: nftShadowPulse 6s ease-in-out infinite;
}
.nftCard--paused + .nftCardShadow,
.nftCardStage .nftCard--paused ~ .nftCardShadow {
  animation-play-state: paused;
}
@keyframes nftShadowPulse {
  0%, 100% { opacity: 0.7; transform: scaleX(1); }
  50%      { opacity: 0.45; transform: scaleX(0.85); }
}

/* Bottom action row: just Owner + Learn more. Side-by-side on desktop
   (equal flex), stacked full-width on mobile. Tighter padding than the
   global mainBody defaults so the row reads as a compact footer chip
   strip instead of two thumb-sized blobs. */
.txlActionRow {
  margin-top: 8px;
}
.txlActionRow .actionButton,
.txlActionRow .actionButtonHelp,
.txlActionRow .txlOwnerBtn {
  flex: 1 1 0;
  padding: 8px 12px;
  min-height: 36px;
  font-size: 12.5px;
  letter-spacing: 0.02em;
  line-height: 1.2;
  text-align: center;
}

/* Owner button — default state inherits the standard .actionButton
   look so it reads as a peer of "Learn more" beside it. Distinct
   variants only kick in for special states:
     --forSale  jade buy-me chip with ↗ glyph (token on the objkt
                marketplace contract — the only time the appearance
                should change visually).
     --mine     subtle green chip when the connected wallet owns it.
     --idle     muted, no pointer, while the owner address loads. */
.txlOwnerBtn {
  text-decoration: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: var(--ad-text-1);
}
.txlOwnerBtn--idle {
  cursor: default;
  opacity: 0.7;
}
.txlOwnerBtn--idle:hover {
  transform: none;
  box-shadow: none;
  background: inherit;
  border-color: inherit;
}
.txlOwnerBtn--mine {
  border-color: var(--ad-green-1, #88c89a);
  background:
    linear-gradient(135deg,
      rgba(136, 200, 154, 0.18) 0%,
      rgba(31, 92, 58, 0.10) 100%);
}
.txlOwnerBtn--forSale {
  border-color: var(--ad-violet-2, rgba(74, 111, 165, 0.55));
  background:
    linear-gradient(135deg,
      rgba(74, 111, 165, 0.12) 0%,
      rgba(74, 111, 165, 0.08) 100%);
  font-weight: 600;
  transition: transform 0.12s ease, box-shadow 0.15s ease,
              background 0.15s ease, border-color 0.15s ease;
}
.txlOwnerBtn--forSale:hover,
.txlOwnerBtn--forSale:focus-visible {
  outline: none;
  transform: translateY(-1px);
  border-color: var(--ad-violet-1, #7dd3c8);
  background:
    linear-gradient(135deg,
      rgba(74, 111, 165, 0.22) 0%,
      rgba(74, 111, 165, 0.14) 100%);
  box-shadow: var(--ad-glow-violet, 0 6px 22px rgba(74, 111, 165, 0.26));
}
.txlOwnerBtn__ext {
  font-size: 0.85em;
  opacity: 0.85;
}

/* ─── Mobile ────────────────────────────────────────────────────────
   Most of the page uses shared classes (.gameInfo / .rowFlex /
   .actionButton) that mainBody.vue already styles for mobile. Only
   the NFT stage + owner badge need a tighter tune on phones. */
@media (max-width: 480px) {
  .nftCardStage {
    /* Shrink the upper bound so the card never dominates the viewport
       on small phones; let it occupy 75 vw if needed. */
    width: clamp(180px, 75vw, 280px);
    margin: 10px auto;
  }
  /* Phones: stack the Owner + Learn more row full-width. Tap targets
     keep ≥40px since stacked column gives them room and they're the
     primary footer actions on this view. */
  .txlActionRow--stack {
    flex-direction: column;
  }
  .txlActionRow--stack .actionButton,
  .txlActionRow--stack .actionButtonHelp,
  .txlActionRow--stack .txlOwnerBtn {
    flex: 1 1 100%;
    width: 100%;
    min-height: 40px;
    padding: 10px 12px;
    font-size: 12.5px;
  }
}
@media (min-width: 481px) and (max-width: 768px) {
  .nftCardStage {
    width: clamp(220px, 55vw, 300px);
  }
}
</style>
